/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import {
  Modifier,
  convertFromHTML,
  ContentState,
  SelectionState,
} from 'draft-js';

/**
 * Internal dependencies
 */
import customImport from './customImport';
import convertStyles from './formatters/convert';
import { EMPTY_LINE_HEIGHT_DATA } from './emptyLineHeight';
import { LIST_BLOCK_TYPES, MAX_LIST_DEPTH } from './constants';

class CustomBlockRenderMap {
  constructor() {
    this._map = {
      unstyled: {
        element: 'div',
        aliasedElements: ['p', 'h1', 'h2', 'h3', 'blockquote', 'pre'],
      },
      'unordered-list-item': {
        element: 'li',
      },
      'ordered-list-item': {
        element: 'li',
      },
    };
  }

  // The only function from immutable.js#Map that Draft's importer needs.
  mapKeys(callback) {
    return Object.keys(this._map).map((key) => callback(key, this._map[key]));
  }
}

function getPastedBlocks(html, existingStyles = []) {
  const hasSemanticList = /<(?:ul|ol|li)(?:\s|>)/i.test(html);
  let pastedContentState;

  if (hasSemanticList) {
    // Draft's native converter merges nested list children into their parent
    // item. Studio's semantic importer retains list type and depth.
    pastedContentState = customImport(html);
  } else {
    // Preserve the established non-list paste normalization path.
    const renderMap = new CustomBlockRenderMap();
    const { contentBlocks, entityMap } = convertFromHTML(
      html,
      undefined /* This has to be undefined to trigger default argument */,
      renderMap
    );
    pastedContentState = ContentState.createFromBlockArray(
      contentBlocks,
      entityMap
    );
  }

  // Now select everything and remove entity from it all
  const entireSelection = selectEverything(pastedContentState);
  const noEntityContent = Modifier.applyEntity(
    pastedContentState,
    entireSelection,
    null
  );

  // Then walk through the content, normalizing supported inline styles and
  // block types and clamping semantic list depth.
  const newContentBlocks = noEntityContent.getBlocksAsArray();
  let updatedContentState = noEntityContent;

  newContentBlocks.forEach((contentBlock) => {
    const blockSelection = SelectionState.createEmpty(contentBlock.getKey());
    const blockType = contentBlock.getType();
    updatedContentState = convertStyles(
      contentBlock,
      blockSelection,
      updatedContentState
    );

    if (blockType !== 'unstyled' && !LIST_BLOCK_TYPES.includes(blockType)) {
      const entireBlockSelection = blockSelection.merge({
        focusOffset: contentBlock.getLength(),
      });
      updatedContentState = Modifier.setBlockType(
        updatedContentState,
        entireBlockSelection,
        'unstyled'
      );
    }

    if (
      LIST_BLOCK_TYPES.includes(blockType) &&
      contentBlock.getDepth() > MAX_LIST_DEPTH
    ) {
      const blockMap = updatedContentState.getBlockMap();
      const currentBlock = blockMap.get(contentBlock.getKey());
      updatedContentState = updatedContentState.set(
        'blockMap',
        blockMap.set(
          contentBlock.getKey(),
          currentBlock.set('depth', MAX_LIST_DEPTH)
        )
      );
    }
  });

  // Authoring metadata belongs only to the trusted internal clipboard path.
  updatedContentState = updatedContentState.set(
    'blockMap',
    updatedContentState
      .getBlockMap()
      .map((block) =>
        block.set('data', block.getData().remove(EMPTY_LINE_HEIGHT_DATA))
      )
  );

  // Finally, apply existing styles if any
  const everythingSelection = selectEverything(updatedContentState);
  existingStyles.forEach((existingStyle) => {
    updatedContentState = Modifier.applyInlineStyle(
      updatedContentState,
      everythingSelection,
      existingStyle
    );
  });

  return updatedContentState.getBlockMap();
}

export default getPastedBlocks;

function selectEverything(contentState) {
  const firstBlock = contentState.getFirstBlock();
  const lastBlock = contentState.getLastBlock();
  return SelectionState.createEmpty(firstBlock.getKey()).merge({
    focusKey: lastBlock.getKey(),
    focusOffset: lastBlock.getLength(),
  });
}
